import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getOrder } from '../lib/api';
import { getPaymentLabel, getPaymentReferenceId, getShippingMethodDescription } from '../lib/orderDisplay';
import AccountSidebar from '../components/AccountSidebar';
import MapPreview from '../components/MapPreview';
import './OrderStatusPage.css';

/* ── Icons (reused style from OrderConfirmationPage) ── */
function CheckCircleIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12.5 11 15.5 16 9" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function TruckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}
function ChevronLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
function XCircleIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

/* ── Status timeline config ──────────────────────────────────────────────
   Each non-cancelled order moves through these steps in order. We derive
   how far along the order is from `status` and light up every step up to
   and including the current one. */
const STEPS = [
  { key: 'confirmed',        label: 'Confirmed',        sub: 'Order placed' },
  { key: 'processing',       label: 'Processing',       sub: 'Getting your plant ready' },
  { key: 'shipped',          label: 'Shipped',          sub: 'On its way to you' },
  { key: 'out_for_delivery', label: 'Out for Delivery', sub: 'Almost there!' },
  { key: 'delivered',        label: 'Delivered',        sub: 'Arrived' },
];

function StatusTimeline({ status, deliveredOn }) {
  if (status === 'cancelled') {
    return (
      <div className="osp-timeline osp-timeline--cancelled">
        <div className="osp-timeline__cancel-icon"><XCircleIcon /></div>
        <div>
          <p className="osp-timeline__cancel-title">Order cancelled</p>
          <p className="osp-timeline__cancel-sub">This order will not be processed further.</p>
        </div>
      </div>
    );
  }

  const currentIdx = STEPS.findIndex(s => s.key === status);
  const activeIdx = currentIdx === -1 ? 0 : currentIdx;

  return (
    <div className="osp-timeline">
      {STEPS.map((step, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <div key={step.key} className={`osp-step${done ? ' osp-step--done' : ''}${active ? ' osp-step--active' : ''}`}>
            <div className="osp-step__rail">
              <span className="osp-step__dot">{done ? '✓' : i + 1}</span>
              {i < STEPS.length - 1 && <span className="osp-step__line" />}
            </div>
            <div className="osp-step__body">
              <p className="osp-step__label">{step.label}</p>
              <p className="osp-step__sub">{step.key === 'delivered' && deliveredOn ? deliveredOn : step.sub}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function OrderStatusPage({ user, onLogout }) {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [copied, setCopied]     = useState(false);

  useEffect(() => {
    if (!user) { navigate('/account'); return; }
    fetchOrder();
  }, [user, orderId]); // eslint-disable-line

  const fetchOrder = async () => {
    setLoading(true);
    setFetchError('');
    try {
      const data = await getOrder(orderId);
      setOrder(data);
    } catch (err) {
      setFetchError(err.message || 'Failed to load order.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTracking = () => {
    if (!order?.tracking) return;
    navigator.clipboard?.writeText(order.tracking);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="osp-page-outer">
      <AccountSidebar user={user} onLogout={onLogout} active="orders" />

      <main className="osp-main">
        <Link to="/account/orders" className="osp-back">
          <ChevronLeftIcon /> Back to orders
        </Link>

        {loading && (
          <div className="osp-skel">
            <div className="skel" style={{ width: '50%', height: 28, borderRadius: 8 }} />
            <div className="skel" style={{ width: '30%', height: 13, borderRadius: 5, marginTop: 10 }} />
            <div className="osp-skel__card">
              <div className="osp-skel__steps">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="osp-skel__step">
                    <div className="osp-skel__dot skel" />
                    <div className="osp-skel__step-text">
                      <div className="skel" style={{ width: 80, height: 13, borderRadius: 5 }} />
                      <div className="skel" style={{ width: 55, height: 11, borderRadius: 4, marginTop: 4 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="osp-skel__card">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="osp-skel__item">
                  <div className="osp-skel__item-img skel" />
                  <div className="osp-skel__item-info">
                    <div className="skel" style={{ width: '60%', height: 13, borderRadius: 5 }} />
                    <div className="skel" style={{ width: '35%', height: 12, borderRadius: 5, marginTop: 6 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && fetchError && (
          <div className="osp-empty">
            <span className="osp-empty__emoji">⚠️</span>
            <h3>Couldn't load this order</h3>
            <p>{fetchError}</p>
            <button className="osp-btn osp-btn--primary" onClick={fetchOrder} style={{ display: 'inline-flex', width: 'auto', padding: '0 28px', marginTop: '12px' }}>
              Retry
            </button>
          </div>
        )}

        {!loading && !fetchError && order && (() => {
          const {
            orderId: id, items = [], subtotal = 0, discount = 0, shipping = 0, codFee = 0,
            finalTotal = 0, promoCode, address = {}, deliveryCoords, payment,
            paymentStatus, createdAt, updatedAt, status, tracking,
          } = order;

          const placedOn = createdAt
            ? new Date(createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
            : null;

          const deliveredOn = status === 'delivered' && updatedAt
            ? new Date(updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
            : null;

          const paymentLabel = getPaymentLabel(order);
          const paymentReference = getPaymentReferenceId(order);
          const shippingMethodDescription = getShippingMethodDescription(order);

          const fullName = `${address.firstName || ''} ${address.lastName || ''}`.trim();
          const hasCoords = deliveryCoords && deliveryCoords.lat != null && deliveryCoords.lng != null;

          return (
            <div className="osp-inner">
              {/* ── Left column ── */}
              <div className="osp-left">

                <div className="osp-hero">
                  <div className={`osp-hero__check${status === 'cancelled' ? ' osp-hero__check--cancelled' : ''}`}>
                    {status === 'cancelled' ? <XCircleIcon /> : <CheckCircleIcon />}
                  </div>
                  <p className="osp-hero__conf">Order #{id}</p>
                  <h1 className="osp-hero__title">
                    {status === 'cancelled' ? 'Order cancelled' : `Hi${fullName ? `, ${fullName.split(' ')[0]}` : ''}`}
                  </h1>
                  {placedOn && <p className="osp-hero__date">Placed on {placedOn}{deliveredOn ? ` · Delivered on ${deliveredOn}` : ''}</p>}
                </div>

                {/* ── Status timeline ── */}
                <section className="osp-card">
                  <h2 className="osp-card__title">Order status</h2>
                  <StatusTimeline status={status} deliveredOn={deliveredOn} />
                  {tracking && status !== 'cancelled' && (
                    <div className="osp-tracking">
                      <span className="osp-pill"><TruckIcon /> {shippingMethodDescription}</span>
                      <button className="osp-tracking__copy" onClick={handleCopyTracking}>
                        <span className="osp-tracking__num">{tracking}</span>
                        <CopyIcon /> {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  )}
                </section>

                {/* ── Delivery location map ── */}
                {hasCoords && (
                  <section className="osp-card">
                    <h2 className="osp-card__title">Delivery location</h2>
                    <p className="osp-card__sub">Your plant will arrive at the pinned location below.</p>
                    <div className="osp-map-wrap">
                      <MapPreview lat={deliveryCoords.lat} lng={deliveryCoords.lng} />
                    </div>
                    <p className="osp-map-address">
                      <PinIcon /> {address.address}{address.apartment ? `, ${address.apartment}` : ''}, {address.city}, {address.state} {address.pincode}
                    </p>
                  </section>
                )}

                {/* ── Order details ── */}
                <section className="osp-card">
                  <h2 className="osp-card__title">Order details</h2>
                  <div className="osp-details-grid">
                    <div className="osp-details-col">
                      <h3 className="osp-details-label">Contact information</h3>
                      <p className="osp-details-text">{address.email || address.phone}</p>

                      <h3 className="osp-details-label">Shipping address</h3>
                      <p className="osp-details-text">
                        {fullName}<br />
                        {address.address}{address.apartment ? `, ${address.apartment}` : ''}<br />
                        {address.city}, {address.state} {address.pincode}<br />
                        India<br />
                        {address.phone}
                      </p>

                      <h3 className="osp-details-label">Shipping method</h3>
                      <p className="osp-details-text">{shippingMethodDescription}</p>
                    </div>
                    <div className="osp-details-col">
                      <h3 className="osp-details-label">Payment method</h3>
                      <p className="osp-details-text">
                        {paymentLabel}
                        {paymentStatus && (
                          <span className={`osp-pay-status osp-pay-status--${paymentStatus}`}>
                            {paymentStatus === 'paid' ? 'Paid' : paymentStatus === 'failed' ? 'Failed' : 'Pending'}
                          </span>
                        )}
                      </p>
                      {paymentReference && (
                        <>
                          <h3 className="osp-details-label">Payment reference</h3>
                          <p className="osp-details-text osp-details-text--mono">{paymentReference}</p>
                        </>
                      )}

                      <h3 className="osp-details-label">Billing address</h3>
                      <p className="osp-details-text">
                        {fullName}<br />
                        {address.address}{address.apartment ? `, ${address.apartment}` : ''}<br />
                        {address.city}, {address.state} {address.pincode}<br />
                        India
                      </p>
                    </div>
                  </div>
                </section>

                <div className="osp-actions">
                  {status === 'delivered' && (
                    <Link to="/collections/plants" className="osp-btn osp-btn--primary">Buy again</Link>
                  )}
                  <button className="osp-btn">Download invoice</button>
                  <button className="osp-btn" onClick={() => navigate('/collections/plants')}>Continue shopping</button>
                </div>
              </div>

              {/* ── Right column / summary ── */}
              <aside className="osp-right">
                <div className="osp-summary">
                  <div className="osp-summary__items">
                    {items.map((item, idx) => {
                      const isBundle = item.itemType === 'bundle';
                      const variants = [item.selectedSize, item.selectedPlanter, item.selectedColor].filter(Boolean);
                      return (
                        <div key={idx} className="osp-summary__item">
                          <div className="osp-summary__img-wrap">
                            <span className="osp-summary__badge">{item.qty}</span>
                            <div className="osp-summary__img">
                              {item.image
                                ? <img src={item.image} alt={item.name}
                                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                : null}
                              <div className="osp-summary__img-fallback" style={item.image ? { display: 'none' } : {}}>🌿</div>
                            </div>
                          </div>
                          <div className="osp-summary__item-body">
                            <p className="osp-summary__item-name">
                              {item.name}
                              {isBundle && <span className="osp-bundle-badge">Bundle</span>}
                            </p>
                            {variants.length > 0 && (
                              <p className="osp-summary__item-variants">
                                {variants.join(' · ')}
                              </p>
                            )}
                            {/* Bundle: show each plant with its variant image + details */}
                            {isBundle && item.bundleItems && item.bundleItems.length > 0 && (
                              <div className="osp-bundle-plants">
                                {item.bundleItems.map((bi, i) => {
                                  const biVariants = [bi.selectedSize, bi.selectedPlanter, bi.selectedColor].filter(Boolean);
                                  return (
                                    <div key={i} className="osp-bundle-plant">
                                      <div className="osp-bundle-plant__img-wrap">
                                        {bi.image
                                          ? <img src={bi.image} alt={bi.name} className="osp-bundle-plant__img"
                                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                          : null}
                                        <div className="osp-bundle-plant__fallback" style={bi.image ? { display: 'none' } : {}}>🌿</div>
                                      </div>
                                      <div className="osp-bundle-plant__details">
                                        <span className="osp-bundle-plant__name">{bi.name}</span>
                                        {biVariants.length > 0 && (
                                          <span className="osp-bundle-plant__variants">{biVariants.join(' · ')}</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <p className="osp-summary__item-price">₹{(item.price * item.qty).toLocaleString()}</p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="osp-divider" />

                  <div className="osp-summary__lines">
                    <div className="osp-summary__line">
                      <span>Subtotal</span>
                      <span>₹{subtotal.toLocaleString()}</span>
                    </div>
                    {discount > 0 && (
                      <div className="osp-summary__line osp-summary__line--disc">
                        <span>Discount{promoCode ? ` (${promoCode})` : ''}</span>
                        <span>−₹{discount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="osp-summary__line">
                      <span>Shipping</span>
                      <span>{shipping === 0 ? <span className="osp-free">Free</span> : `₹${shipping}`}</span>
                    </div>
                    {codFee > 0 && (
                      <div className="osp-summary__line">
                        <span>Cash on Delivery fee</span>
                        <span>₹{codFee.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  <div className="osp-divider" />

                  <div className="osp-summary__total">
                    <div>
                      <span className="osp-summary__total-label">Total</span>
                      <span className="osp-summary__currency">INR</span>
                    </div>
                    <span className="osp-summary__total-amount">₹{finalTotal.toLocaleString()}</span>
                  </div>
                </div>
              </aside>
            </div>
          );
        })()}
      </main>
    </div>
  );
}